import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Minus } from 'lucide-react'
import { getCoinIcon, parseQuantity } from '@/lib/common'
import { toFixed, decdiv, decmul } from '@/lib/decimal'
import type { Pair, TokenInfo } from '@/types'

interface LiquidityItem {
  pair: Pair
  token0: TokenInfo
  token1: TokenInfo
  userToken: number
  totalToken: number
  ratio: string
  token0Quantity: string
  token1Quantity: string
  cost0: string
  cost1: string
}

interface MyLiquidityProps {
  liquidityList: LiquidityItem[]
  onAddLiquidity: (pair: Pair) => void
  onRemoveLiquidity: (item: LiquidityItem) => void
}

export function MyLiquidity({ liquidityList, onAddLiquidity, onRemoveLiquidity }: MyLiquidityProps) {
  const { t } = useTranslation()
  const [profitToken, setProfitToken] = useState<Record<number, boolean>>({})

  if (liquidityList.length === 0) return null

  const calcProfit = (item: LiquidityItem, useToken1: boolean): string => {
    const reserve0 = parseFloat(parseQuantity(item.token0.reserve).amount)
    const reserve1 = parseFloat(parseQuantity(item.token1.reserve).amount)
    if (reserve0 === 0 || reserve1 === 0) return '0'

    const price = useToken1 ? decdiv(reserve0, reserve1) : decdiv(reserve1, reserve0)
    const currentA = parseFloat(item.token0Quantity)
    const currentB = parseFloat(item.token1Quantity)
    const costA = parseFloat(item.cost0)
    const costB = parseFloat(item.cost1)

    if (useToken1) {
      const left = parseFloat(decmul(price, currentB)) + currentA
      const right = parseFloat(decmul(price, costB)) + costA
      return toFixed(left - right, item.token0.precision)
    } else {
      const left = parseFloat(decmul(price, currentA)) + currentB
      const right = parseFloat(decmul(price, costA)) + costB
      return toFixed(left - right, item.token1.precision)
    }
  }

  return (
    <div className="mt-[30px]">
      <div className="bg-white rounded-[26px] shadow-[0_0_8px_1px_rgba(61,98,50,0.14)] px-[16px] py-[24px]">
        <h3 className="text-[16px] font-bold text-foreground mb-[16px]">{t('swap.myLiquidity')}</h3>

        <div className="space-y-3">
        {liquidityList.map((item) => {
          const useToken0 = profitToken[item.pair.id] !== false
          const profit = calcProfit(item, useToken0)
          const profitNum = parseFloat(profit)
          const profitCoin = useToken0 ? item.token0.coin : item.token1.coin

          return (
            <div key={item.pair.id} className="bg-white rounded-2xl border border-[#e5e5e5] p-4">
              {/* Header: pair name + actions */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    <img src={getCoinIcon(item.token0.contract, item.token0.coin)} className="w-[28px] h-[28px] rounded-full bg-gray-100 border-2 border-white" />
                    <img src={getCoinIcon(item.token1.contract, item.token1.coin)} className="w-[28px] h-[28px] rounded-full bg-gray-100 border-2 border-white" />
                  </div>
                  <span className="text-[15px] font-semibold text-foreground">
                    {item.token0.coin}/{item.token1.coin}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onAddLiquidity(item.pair)}
                    className="p-1.5 rounded-full border border-primary text-primary hover:bg-primary hover:text-white transition-colors"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onRemoveLiquidity(item)}
                    className="p-1.5 rounded-full border border-red-400 text-red-400 hover:bg-red-400 hover:text-white transition-colors"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Details */}
              <div className="space-y-1.5 text-[13px]">
                <div className="flex justify-between">
                  <span className="text-nav">{t('swap.liquidityRatio')}</span>
                  <span className="text-foreground">{item.ratio}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-nav">{t('swap.currentLiquidity')}</span>
                  <span className="text-foreground">
                    {item.token0Quantity} {item.token0.coin} + {item.token1Quantity} {item.token1.coin}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-nav">{t('swap.costBasis')}</span>
                  <span className="text-foreground">
                    {item.cost0} {item.token0.coin} + {item.cost1} {item.token1.coin}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-nav">{t('swap.profit')}</span>
                  <div className="flex items-center gap-1">
                    <span className={profitNum >= 0 ? 'text-primary' : 'text-red-500'}>
                      {profitNum >= 0 ? '+' : ''}{profit} {profitCoin}
                    </span>
                    <button
                      onClick={() => setProfitToken((prev) => ({ ...prev, [item.pair.id]: !useToken0 }))}
                      className="text-[12px] text-nav hover:text-primary transition-colors underline"
                    >
                      {useToken0 ? item.token1.coin : item.token0.coin}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        })}
        </div>
      </div>
    </div>
  )
}

export type { LiquidityItem }
